/**
 * contactsService.js
 * 
 * Servicio centralizado para gestión de contactos.
 * Actúa como interfaz entre el frontend (React) y el backend (API).
 * 
 * Características:
 * - Autenticación automática con authFetchJson/authFetch
 * - Validación de usuario con requireAuth()
 * - Manejo de errores específicos por código HTTP
 * - Deduplicación de requests simultáneos
 * - JSDoc completo para autocompletado
 */

import { authFetchJson, authFetch } from './authFetch';
import { auth } from '../firebase';
import { API_URL } from '../config/api';

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Valida que el usuario esté autenticado
 * @returns {import('firebase/auth').User} Usuario autenticado
 * @throws {Error} Si no hay usuario logueado
 */
function requireAuth() {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("Usuario no autenticado. Por favor inicia sesión.");
    }
    return user;
}

/**
 * Construye URL con query params, ignorando valores null/undefined
 * @param {string} baseUrl - URL base
 * @param {Object} params - Objeto de parámetros
 * @returns {string} URL completa con params
 */
function buildUrl(baseUrl, params = {}) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
        }
    });
    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/** Mapa para deduplicar requests simultáneos */
const requestQueue = new Map();

/**
 * Previene requests duplicados simultáneos
 * @param {string} key - Identificador único del request
 * @param {Function} fn - Función async a ejecutar
 * @returns {Promise<any>}
 */
async function dedupRequest(key, fn) {
    if (requestQueue.has(key)) {
        console.log(`[Dedup] Esperando request existente: ${key}`);
        return await requestQueue.get(key);
    }

    const promise = fn();
    requestQueue.set(key, promise);

    try {
        return await promise;
    } finally {
        requestQueue.delete(key);
    }
}

/**
 * Genera mensaje de error descriptivo según código HTTP
 * @param {number} status - Código de estado HTTP
 * @param {string} context - Contexto de la operación
 * @returns {string} Mensaje de error descriptivo
 */
function getErrorMessage(status, context) {
    switch (status) {
        case 401:
            return 'Sesión expirada. Por favor inicia sesión nuevamente.';
        case 403:
            return `No tienes permisos para ${context}.`;
        case 404:
            return 'Recurso no encontrado.';
        case 429:
            return 'Demasiadas solicitudes. Intenta en unos minutos.';
        case 500:
            return 'Error del servidor. Intenta nuevamente.';
        case 503:
            return 'Servicio temporalmente no disponible.';
        default:
            return `Error ${status}: No se pudo ${context}.`;
    }
}

// ═══════════════════════════════════════════════════════════════
// CONTACTOS - CRUD
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene la lista de contactos del usuario actual
 * @returns {Promise<{contacts: Array, total: number}>}
 */
export async function getContacts() {
    requireAuth();
    const res = await authFetchJson(`${API_URL}/contacts`, {}, { retry: 1 });
    // 🔧 FIX: el backend devuelve { items, hasMore, lastId }. Normalizamos para los
    // consumidores que esperan { success, contacts } (MassMessagingPage,
    // campaignExecutionService -recipients-, useCampaignData) sin romper { items }.
    const items = (res && (res.items || res.contacts)) || [];
    return { success: res?.success !== false, contacts: items, items, hasMore: res?.hasMore, lastId: res?.lastId };
}

/**
 * Actualiza el nombre de un contacto
 * @param {string} id - ID del contacto
 * @param {string} name - Nuevo nombre
 * @returns {Promise<Object>}
 * @throws {Error} Si id o name son inválidos
 */
export async function updateContactName(id, name) {
    requireAuth();

    if (!id || typeof id !== 'string') {
        throw new Error("ID de contacto inválido.");
    }
    if (!name || typeof name !== 'string' || name.trim().length === 0) {
        throw new Error("El nombre no puede estar vacío.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/${id}/name`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name.trim() }),
        },
        { retry: 1 }
    );
}

/**
 * Actualiza múltiples campos de un contacto (nombre, redes sociales, notas, etc.)
 * @param {string} id - ID del contacto
 * @param {Object} updates - Objeto con los campos a actualizar
 * @returns {Promise<Object>}
 * @throws {Error} Si id o updates son inválidos
 */
export async function updateContactDetails(id, updates) {
    requireAuth();

    if (!id || typeof id !== 'string') {
        throw new Error("ID de contacto inválido.");
    }
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
        throw new Error("No hay datos para actualizar.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/${id}`,
        {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates),
        },
        { retry: 1 }
    );
}

/**
 * Actualiza la etapa (stage) de un contacto
 * @param {string} id - ID del contacto
 * @param {string} stage - Nueva etapa
 * @returns {Promise<Object>}
 */
export async function changeContactStage(id, stage) {
    requireAuth();

    if (!id || typeof id !== 'string') {
        throw new Error("ID de contacto inválido.");
    }

    return await dedupRequest(`stage:${id}`, async () => {
        return await authFetchJson(
            `${API_URL}/contacts/${id}/stage`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ stage }),
            },
            { retry: 1 }
        );
    });
}

/**
 * Crea o actualiza (upsert) un contacto local
 * @param {Object} params - Datos del contacto
 * @param {string} params.name - Nombre del contacto
 * @param {string} params.phone - Número de teléfono
 * @param {string} [params.number] - Número alternativo
 * @param {string} [params.email] - Email
 * @param {string} [params.stage] - Etapa inicial
 * @returns {Promise<Object>}
 */
export async function createContact({ name, phone, number, email, stage, facebook, instagram, notes }) {
    requireAuth();

    if (!name || typeof name !== 'string' || name.trim().length === 0) {
        throw new Error("El nombre del contacto es requerido.");
    }
    if (!phone && !number) {
        throw new Error("Se requiere un número de teléfono.");
    }

    return await authFetchJson(
        `${API_URL}/contacts`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name.trim(), phone, number, email, stage, facebook, instagram, notes })
        },
        { retry: 1 }
    );
}

/**
 * Elimina un contacto del sistema local
 * @param {string} contactId - ID del contacto a eliminar
 * @returns {Promise<Object>}
 */
export async function deleteContact(contactId) {
    requireAuth();

    if (!contactId) {
        throw new Error("ID de contacto requerido.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/${contactId}`,
        { method: 'DELETE' },
        { retry: 1 }
    );
}

/**
 * Elimina múltiples contactos del sistema local
 * @param {string[]} contactIds - Array de IDs a eliminar
 * @returns {Promise<Object>}
 */
export async function deleteContactsBatch(contactIds) {
    requireAuth();

    if (!Array.isArray(contactIds) || contactIds.length === 0) {
        throw new Error("Se requiere al menos un ID de contacto.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/batch-delete`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactIds })
        },
        { retry: 1 }
    );
}

// ═══════════════════════════════════════════════════════════════
// SINCRONIZACIÓN
// ═══════════════════════════════════════════════════════════════

/**
 * Sincroniza contactos seleccionados con Google
 * @param {string[]} contactIds - IDs de contactos a sincronizar
 * @returns {Promise<Object>}
 */
export async function syncContacts(contactIds) {
    requireAuth();

    if (!Array.isArray(contactIds)) {
        throw new Error("Se requiere un array de IDs.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/sync-google`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactIds }),
        },
        { retry: 1 }
    );
}

/**
 * Sincroniza contactos de Google con la aplicación
 * @returns {Promise<Object>}
 */
export async function syncGoogleContacts() {
    const user = requireAuth();
    return await authFetchJson(
        `${API_URL}/google/sync-contacts`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.uid }),
        },
        { retry: 1 }
    );
}

/**
 * Importa contactos de Google a la base de datos local
 * @returns {Promise<Object>}
 */
export async function syncGoogleToLocal() {
    requireAuth();
    return await authFetchJson(
        `${API_URL}/google/sync-to-local`,
        { method: 'POST' },
        { retry: 1 }
    );
}

// ═══════════════════════════════════════════════════════════════
// GOOGLE CONTACTS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene contactos de Google (sin guardar localmente)
 * @returns {Promise<{googleContacts: Array}>}
 */
export async function getGoogleContacts() {
    requireAuth();
    return await authFetchJson(`${API_URL}/google/contacts`, {}, { retry: 1 });
}

/**
 * Elimina múltiples contactos de Google
 * @param {string[]} contactIds - IDs de recursos de Google
 * @returns {Promise<Object>}
 */
export async function deleteGoogleContactsBatch(contactIds) {
    requireAuth();

    if (!Array.isArray(contactIds) || contactIds.length === 0) {
        throw new Error("Se requiere al menos un ID.");
    }

    return await authFetchJson(
        `${API_URL}/google/contacts/batch-delete`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactIds }),
        },
        { retry: 1 }
    );
}

/**
 * Crea un contacto en Google
 * @param {Object} params - Datos del contacto
 * @returns {Promise<Object>}
 */
export async function createGoogleContact({ name, phone, number, email }) {
    requireAuth();

    if (!name || typeof name !== 'string') {
        throw new Error("El nombre es requerido.");
    }

    return await authFetchJson(
        `${API_URL}/google/contacts`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, phone, number, email })
        },
        { retry: 1 }
    );
}

/**
 * Desvincula la cuenta de Google del usuario
 * @returns {Promise<Object>}
 */
export async function disconnectGoogle() {
    const user = requireAuth();
    return await authFetchJson(
        `${API_URL}/google/disconnect`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.uid }),
        },
        { retry: 1 }
    );
}

/**
 * Obtiene la URL para iniciar conexión con Google
 * @param {string} userId - ID del usuario
 * @returns {string} URL de autenticación
 */
export function getGoogleAuthUrl(userId) {
    const absoluteBase = API_URL.replace(/\/api$/, '').replace(/\/$/, '');
    return `${absoluteBase}/api/google?userId=${encodeURIComponent(userId)}`;
}

// ═══════════════════════════════════════════════════════════════
// WHATSAPP - IMPORT/EXPORT
// ═══════════════════════════════════════════════════════════════

/**
 * Exporta contactos de WhatsApp a CSV y descarga automáticamente
 * @param {'contacts'|'chats'} [source='contacts'] - Fuente de datos
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.save=true] - Guardar en base de datos
 * @param {string} [options.segmentName] - Nombre del segmento
 * @returns {Promise<boolean>} true si la descarga fue exitosa
 */
export async function exportWhatsappCsv(source = 'contacts', options = {}) {
    requireAuth();

    const params = { source, country: 'cr' };
    if (source === 'contacts') {
        if (options.save !== false) params.save = 'true';
        if (options.segmentName) params.segmentName = options.segmentName;
    }

    const res = await authFetch(
        buildUrl(`${API_URL}/contacts/export-whatsapp-csv`, params),
        { method: 'GET' },
        { retry: 1 }
    );

    if (!res.ok) {
        throw new Error(getErrorMessage(res.status, 'exportar contactos'));
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp_${source}_${Date.now()}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return true;
}

/**
 * Importa contactos de WhatsApp a la aplicación
 * @param {Object} [options] - Opciones de importación
 * @param {string} [options.country='cr'] - Código de país
 * @param {string} [options.prefix] - Prefijo telefónico
 * @param {number} [options.exactTotalLength] - Longitud exacta del número
 * @param {string} [options.segmentName] - Nombre del segmento destino
 * @returns {Promise<Object>}
 */
export async function importWhatsappContacts({ country = 'cr', prefix, exactTotalLength, segmentName } = {}) {
    requireAuth();

    const body = { country };
    if (prefix) body.prefix = prefix;
    if (exactTotalLength) body.exactTotalLength = exactTotalLength;
    if (segmentName) body.segmentName = segmentName;

    return await authFetchJson(
        `${API_URL}/contacts/import-whatsapp-contacts`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        },
        { retry: 1 }
    );
}

// ═══════════════════════════════════════════════════════════════
// DUPLICADOS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene grupos de contactos duplicados
 * @returns {Promise<{duplicates: Array}>}
 */
export async function getDuplicateGroups() {
    requireAuth();
    return await authFetchJson(`${API_URL}/contacts/duplicates`, {}, { retry: 1 });
}

/**
 * Escaneo rápido de duplicados
 * @param {Object} [options] - Opciones de escaneo
 * @param {number} [options.limit=50] - Límite de resultados
 * @param {number} [options.min=2] - Mínimo de repeticiones
 * @returns {Promise<Object>}
 */
export async function scanDuplicates({ limit = 50, min = 2 } = {}) {
    requireAuth();
    return await authFetchJson(
        buildUrl(`${API_URL}/contacts/duplicates/scan`, { limit, min }),
        {},
        { retry: 1 }
    );
}

/**
 * Resuelve duplicados manteniendo un contacto y eliminando el resto
 * @param {string} phone - Número de teléfono duplicado
 * @param {string} keepId - ID del contacto a conservar
 * @returns {Promise<Object>}
 */
export async function resolveDuplicates(phone, keepId) {
    requireAuth();

    if (!phone || !keepId) {
        throw new Error("Se requiere phone y keepId.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/duplicates/resolve`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phone, keepId })
        },
        { retry: 1 }
    );
}

/**
 * Resuelve duplicados en lote
 * @param {Array<{phone: string, keepId: string}>} operations - Operaciones a ejecutar
 * @returns {Promise<Object>}
 */
export async function resolveDuplicatesBatch(operations) {
    requireAuth();

    if (!Array.isArray(operations) || operations.length === 0) {
        throw new Error("Se requiere al menos una operación.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/duplicates/resolve-batch`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ operations })
        },
        { retry: 1 }
    );
}

/**
 * Elimina duplicados por números de teléfono
 * @param {string[]} phones - Números a procesar
 * @param {Object} [options] - Opciones
 * @param {boolean} [options.keepOne=true] - Conservar uno de cada
 * @returns {Promise<Object>}
 */
export async function deleteDuplicatesByPhones(phones, { keepOne = true } = {}) {
    requireAuth();

    if (!Array.isArray(phones) || phones.length === 0) {
        throw new Error("Se requiere al menos un número.");
    }

    return await authFetchJson(
        `${API_URL}/contacts/duplicates/delete-by-phones`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phones, keepOne })
        },
        { retry: 1 }
    );
}

// ═══════════════════════════════════════════════════════════════
// MENSAJERÍA
// ═══════════════════════════════════════════════════════════════

/**
 * Envía mensaje masivo a contactos de Google
 * @param {string} message - Contenido del mensaje
 * @param {number} [delay=2000] - Delay entre mensajes (ms)
 * @param {string} [contactFilter='all'] - Filtro de contactos
 * @returns {Promise<Object>}
 */
export async function sendMassMessageToGoogleContacts(message, delay = 2000, contactFilter = 'all') {
    const user = requireAuth();

    if (!message || typeof message !== 'string' || message.trim().length === 0) {
        throw new Error("El mensaje no puede estar vacío.");
    }

    return await authFetchJson(
        `${API_URL}/google/send-mass-message`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                userId: user.uid,
                message: message.trim(),
                delay,
                contactFilter
            }),
        },
        { retry: 1 }
    );
}

/**
 * Envía mensaje individual a un contacto
 * @param {string} phoneNumber - Número de destino (formato internacional)
 * @param {string} message - Contenido del mensaje
 * @param {string[]} [images=[]] - URLs de imágenes a adjuntar
 * @param {string|null} [campaignId=null] - ID de campaña opcional
 * @param {string|null} [idempotencyKey=null] - Clave para prevenir duplicados
 * @returns {Promise<Object>}
 * @example
 * await sendIndividualMessage('+50612345678', 'Hola!', [], null, 'mass:123:456');
 */
export async function sendIndividualMessage(phoneNumber, message, images = [], campaignId = null, idempotencyKey = null) {
    const user = requireAuth();

    // Validaciones
    if (!phoneNumber || typeof phoneNumber !== 'string') {
        throw new Error("Número de teléfono inválido.");
    }
    if (!message || typeof message !== 'string' || message.trim().length === 0) {
        throw new Error("El mensaje no puede estar vacío.");
    }
    if (message.length > 4096) {
        throw new Error("El mensaje es demasiado largo (máx. 4096 caracteres).");
    }
    if (!Array.isArray(images)) {
        throw new Error("El parámetro 'images' debe ser un array.");
    }

    // Deduplicar si hay idempotencyKey
    const key = idempotencyKey || `send:${phoneNumber}:${Date.now()}`;

    return await dedupRequest(key, async () => {
        return await authFetchJson(
            `${API_URL}/whatsapp/send-message`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId: user.uid,
                    phoneNumber,
                    message: message.trim(),
                    images,
                    campaignId,
                    idempotencyKey
                }),
            },
            { retry: 1 }
        );
    });
}

// ═══════════════════════════════════════════════════════════════
// UTILIDADES
// ═══════════════════════════════════════════════════════════════

/**
 * Resetea el contador de contactos al valor real
 * Útil para sincronizar contadores desincronizados
 * @returns {Promise<Object>}
 */
export async function resetContactCounter() {
    requireAuth();
    return await authFetchJson(
        `${API_URL}/contacts/reset-counter`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        },
        { retry: 1 }
    );
}
